import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { distinctUntilChanged, Observable, switchMap } from 'rxjs';
import { debounceTime, map, shareReplay, startWith, take, takeWhile } from 'rxjs/operators';
import { NetzzuordnungService } from '../../service/netzzuordnung/netzzuordnung.service';
import { NetzzuordnungFilter } from '../../service/netzzuordnung/netzzuordnung-filter';
import { NetzzuordnungExportService } from '../../service/netzzuordnung/netzzuordnung-export.service';
import { NetzbetreiberService } from '../../service/netzbetreiber/netzbetreiber.service';
import { TnbUser } from '../../service/auth/tnb-user';
import { TeilgebietSelectionService } from '../../service/common/teilgebiet/teilgebiet-selection.service';
import { Netzbetreiber } from '../../domain/model/netzbetreiber';
import { ActivatedRoute } from '@angular/router';

const FILTER_DEBOUNCE_TIME = 250;
const FILTER_OPTION_LIMIT = 50;

@Component({
    selector: 'redispatch-netzzuordnungen-filter',
    templateUrl: './netzzuordnungen-filter.component.html',
    styleUrls: [
        './netzzuordnungen-filter.component.scss',
    ],
    standalone: false,
})
export class NetzzuordnungenFilterComponent implements OnInit, OnDestroy {

    stadtwerkeId?: string;

    @Input() initialFilter?: NetzzuordnungFilter;
    @Output() filter = new EventEmitter<NetzzuordnungFilter>();

    filterFormGroup: UntypedFormGroup;
    public profile: TnbUser;
    filteredStadtwerkeId$: Observable<string[]>;
    filteredNetzbetreiber$: Observable<string[]>;
    filteredTeilgebiete$: Observable<string[]>;
    filteredNetzgruppe$: Observable<string[]>;
    filteredOrtsnamen$: Observable<string[]>;
    filteredStationsnamen$: Observable<string[]>;
    filteredTrafoFeldNamen$: Observable<string[]>;
    filteredUuid$: Observable<string[]>;
    filteredTechnischerPlatz$: Observable<string[]>;

    private allNetzbetreiberForWestnetzView$: Observable<Netzbetreiber[]>;
    private componentDestroyed = false;

    private netzzuordnungService = inject(NetzzuordnungService);
    private netzbetreiberService = inject(NetzbetreiberService);
    private route = inject(ActivatedRoute);
    private netzzuordnungExportService = inject(NetzzuordnungExportService);
    private formBuilder = inject(UntypedFormBuilder);
    private teilgebietSelectionService = inject(TeilgebietSelectionService);

    public get isWetznetzUser() {
        return this.profile.netzbetreiberType === 'WESTNETZ';
    }

    async ngOnInit() {
        this.profile = this.route.snapshot.data.profile;
        if (this.profile.netzbetreiberType !== 'WESTNETZ') {
            this.stadtwerkeId = this.teilgebietSelectionService.getSelectedStadtwerkeId();
        }

        this.allNetzbetreiberForWestnetzView$ =
            this.netzbetreiberService.getAlleNetzbetreiber()
                .pipe(
                    take(1),
                    shareReplay(1),
                );

        // Build form and set initial values
        this.filterFormGroup = this.formBuilder.group({
            stadtwerkeId: [
                this.initialFilter.stadtwerkeId,
            ],
            ortsname: [
                this.initialFilter.ortsname,
            ],
            stationsname: [
                this.initialFilter.stationsname,
            ],
            trafoFeldName: [
                this.initialFilter.trafoFeldName,
            ],
            uuid: [
                this.initialFilter.uuid,
            ],
            technischerPlatz: [
                this.initialFilter.technischerPlatz,
            ],
            netzbetreiberName: [
                this.initialFilter.netzbetreiberName,
            ],
            teilgebiet: [
                this.initialFilter.teilgebiet,
            ],
            uuidDate: this.formBuilder.group({
                start: [
                    this.initialFilter.uuidDateStart,
                ],
                end: [
                    this.initialFilter.uuidDateEnd,
                ],
            }),
            netzgruppe: [
                this.initialFilter.netzgruppe,
            ],
        });

        this.filterFormGroup.valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.value),
                takeWhile(() => !this.componentDestroyed),
                map(val => {
                    return new NetzzuordnungFilter(
                        val.stadtwerkeId || this.stadtwerkeId,
                        val.ortsname,
                        val.stationsname,
                        val.trafoFeldName,
                        val.uuid,
                        val.uuidDate?.start,
                        val.uuidDate?.end?.endOf('day'),
                        val.technischerPlatz,
                        val.netzbetreiberName,
                        val.teilgebiet,
                        val.netzgruppe,
                    );
                }),
            )
            .subscribe(val => this.filter.next(val));

        this.filteredOrtsnamen$ = this.filterFormGroup.get('ortsname').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('ortsname').value),
                distinctUntilChanged(),
                switchMap(ort => this.netzzuordnungService.getOrtsnamen(
                    this.stadtwerkeId,
                    ort,
                )),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredStationsnamen$ = this.filterFormGroup.get('stationsname').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('stationsname').value),
                distinctUntilChanged(),
                switchMap(stationsname => this.netzzuordnungService.getStationsnamen(
                    this.stadtwerkeId,
                    null,
                    stationsname,
                )),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredTrafoFeldNamen$ = this.filterFormGroup.get('trafoFeldName').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('trafoFeldName').value),
                distinctUntilChanged(),
                switchMap(trafoFeldName => this.netzzuordnungService.getTrafoFeldNamen(
                    this.stadtwerkeId,
                    this.filterFormGroup.get('ortsname').value,
                    this.filterFormGroup.get('stationsname').value,
                    trafoFeldName,
                )),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredUuid$ = this.filterFormGroup.get('uuid').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('uuid').value),
                distinctUntilChanged(),
                switchMap(uuid => this.netzzuordnungService.getUuid(
                    this.stadtwerkeId,
                    uuid,
                )),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredTechnischerPlatz$ = this.filterFormGroup.get('technischerPlatz').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('technischerPlatz').value),
                distinctUntilChanged(),
                switchMap(technischerPlatz => this.netzzuordnungService.getNetzzuordnungId(
                    this.stadtwerkeId,
                    technischerPlatz,
                )),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredStadtwerkeId$ = this.filterFormGroup.get('stadtwerkeId').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('stadtwerkeId').value),
                map(stadtwerkeIdQuery => stadtwerkeIdQuery?.toLowerCase() || ''),
                distinctUntilChanged(),
                switchMap(stadtwerkeIdQuery => {
                    return this.allNetzbetreiberForWestnetzView$.pipe(
                        map(allNetzbetreiber =>
                            stadtwerkeIdQuery ? allNetzbetreiber.filter(n => n.stadtwerkeId.toLowerCase().includes(stadtwerkeIdQuery)) : allNetzbetreiber,
                        ),
                    );
                }),
                map(netzbetreiber => netzbetreiber.map(n => n.stadtwerkeId)),
                map(options => {
                    return [
                        ...new Set(options),
                    ];
                }),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredNetzbetreiber$ = this.filterFormGroup.get('netzbetreiberName').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('netzbetreiberName').value),
                map(name => name?.toLowerCase() || ''),
                distinctUntilChanged(),
                switchMap(name => {
                    return this.allNetzbetreiberForWestnetzView$.pipe(
                        map(allNetzbetreiber =>
                            name ? allNetzbetreiber.filter(n => n.name.toLowerCase().includes(name)) : allNetzbetreiber,
                        ),
                    );
                }),
                map(netzbetreiber => netzbetreiber.map(n => n.name)),
                map(options => {
                    return [
                        ...new Set(options),
                    ];
                }),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredTeilgebiete$ = this.filterFormGroup.get('teilgebiet').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('teilgebiet').value),
                map(teilgebiet => teilgebiet?.toLowerCase() || ''),
                distinctUntilChanged(),
                switchMap(teilgebiet => {
                    return this.allNetzbetreiberForWestnetzView$.pipe(
                        map(allNetzbetreiber =>
                            teilgebiet ? allNetzbetreiber.filter(n => n.teilgebiet.toLowerCase().includes(teilgebiet)) : allNetzbetreiber,
                        ),
                    );
                }),
                map(netzbetreiber => netzbetreiber.map(n => n.teilgebiet)),
                map(options => {
                    return [
                        ...new Set(options),
                    ];
                }),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredNetzgruppe$ = this.filterFormGroup.get('netzgruppe').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('netzgruppe').value),
                distinctUntilChanged(),
                switchMap(netzgruppe => this.netzzuordnungService.getNetzgruppe(
                    this.stadtwerkeId,
                    netzgruppe,
                )),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );
    }

    ngOnDestroy(): void {
        this.componentDestroyed = true;
    }

    public exportNetzzuordnungen() {
        this.netzzuordnungExportService.exportNetzzuordnungen(
            this.teilgebietSelectionService.getSelectedTeilgebiet(),
        );
    }
}
